/* import __COLOCATED_TEMPLATE__ from './search-bar.hbs'; */
/* RESPONSIBLE TEAM: team-self-serve */

import type AppService from 'embercom/services/app-service';
import { getOwner } from '@ember/application';
import type ApplicationInstance from '@ember/application/instance';
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { type TaskGenerator, timeout } from 'ember-concurrency';
import { keepLatestTask } from 'ember-concurrency-decorators';
import { taskFor } from 'ember-concurrency-ts';
import { ref } from 'ember-ref-bucket';
import ENV from 'embercom/config/environment';
import type Action from 'embercom/objects/global-search/actions/action';
import NavigationAction from 'embercom/objects/global-search/actions/navigation';
import AccountRoutes from 'embercom/objects/global-search/routes/account';
import NewSettingsRoutes from 'embercom/objects/global-search/routes/new-settings';
import type SearchableRoute from 'embercom/objects/global-search/routes/searchable-route';
import SettingsRoutes from 'embercom/objects/global-search/routes/settings';
import type InboxHotkeys from 'embercom/services/inbox-hotkeys';
import { type HotkeysMap } from 'embercom/services/inbox-hotkeys';
import type SearchStateService from 'embercom/services/search-state-service';
import { cached } from 'tracked-toolbox';

interface Args {
  isFromNewSettings?: boolean;
}

interface Signature {
  Args: Args;
  Blocks: any;
}

export default class SettingsSearchBar extends Component<Signature> {
  @service declare intercomEventService: any;
  @service declare inboxHotkeys: InboxHotkeys;
  @service declare appService: AppService;
  @service declare searchStateService: SearchStateService;
  @tracked searchTerm = '';
  @tracked activeItemId?: string;
  @tracked isVisible = false;
  @tracked settingsRoute: SearchableRoute[] = SettingsRoutes[0].children!;
  @tracked accountRoutes: SearchableRoute[] = AccountRoutes;

  readonly hotkeys: HotkeysMap;

  constructor(owner: unknown, args: Args) {
    super(owner, args);
    let settingsRoutes = [...NewSettingsRoutes];
    // this search doesn't support localization yet
    // eslint-disable-next-line @intercom/intercom/no-bare-strings
    let PROACTIVE_SUPPORT_NAME = 'Proactive Support';
    // eslint-disable-next-line @intercom/intercom/no-bare-strings
    let OUTBOUND_NAME = 'Outbound';

    let firstRouteChildren = settingsRoutes[0]?.children;
    let proactiveSupportRoute = firstRouteChildren?.find(
      (route) => route.name === PROACTIVE_SUPPORT_NAME,
    );

    if (firstRouteChildren?.length && proactiveSupportRoute) {
      // When using find(), we get a reference to the object and updating the name here will update the object in original array
      proactiveSupportRoute.name = OUTBOUND_NAME;
    }

    this.settingsRoute = settingsRoutes;
    this.accountRoutes = [];
    this.hotkeys = this.inboxHotkeys.hotkeysMap;
  }

  @ref('settings-search-modal') declare modal: HTMLDivElement;
  @ref('settings-search-toggle') declare searchToggle: HTMLDivElement;

  @cached
  get navigationActions(): Action[] {
    let searchableRoutes = [...this.settingsRoute, ...this.accountRoutes];
    return this.createNavigationActions(undefined, searchableRoutes);
  }

  get keys() {
    return [...this.hotkeys.CommandK.display];
  }

  @cached
  get flattenedActions(): Action[] {
    return this.navigationActions
      .flatMap((action) => action.flattened())
      .filter((action) => action.isEnabled);
  }

  createNavigationActions(parent: Action | undefined, searchableRoutes: Array<SearchableRoute>) {
    let owner = getOwner(this) as ApplicationInstance;

    return searchableRoutes.map((searchableRoute) => {
      let navigationActionInputs = {
        id: `${searchableRoute.route}-${searchableRoute.name}`.replaceAll(/\s|\./g, '-'),
        name: searchableRoute.name,
        synonyms: searchableRoute.synonyms ?? [],
        route: searchableRoute.route,
        routeParams: searchableRoute.routeParams ?? {},
        featureFlags: searchableRoute.requiredFeatureFlags ?? [],
        hideOnFeatureFlags: searchableRoute.hideOnFeatureFlags ?? [],
        showOnlyOnCondition: searchableRoute.showOnlyOnCondition,
        app: this.appService.app,
      };

      let searchAction = new NavigationAction(owner, parent, navigationActionInputs);

      if (searchableRoute.children) {
        this.createNavigationActions(searchAction, searchableRoute.children);
      }

      return searchAction;
    });
  }

  get filteredActions(): Action[] {
    if (!this.searchTerm) {
      return this.flattenedActions.filter((action) => action.isRoot || action.depth === 1);
    }

    let matches = this.flattenedActions.filter((action) => action.matches);

    return matches.sort((a, b) => {
      return a.priority - b.priority;
    });
  }

  get activeItem(): Action | undefined {
    if (this.activeItemId) {
      return this.filteredActions.find((action) => action.id === this.activeItemId);
    } else {
      return undefined;
    }
  }

  set activeItem(action: Action | undefined) {
    this.activeItemId = action?.id;
  }

  @action selectNext() {
    if (this.activeItem) {
      let index = this.filteredActions.findIndex((action) => action.id === this.activeItem?.id);

      if (index + 1 < this.filteredActions.length) {
        this.activeItem = this.filteredActions[index + 1];
      } else {
        this.activeItem = this.filteredActions.firstObject;
      }
    } else {
      this.activeItem = this.filteredActions.firstObject;
    }

    this.activeItem?.didBecomeActive();
  }

  @action selectPrevious() {
    if (this.activeItem) {
      let index = this.filteredActions.findIndex((action) => action.id === this.activeItem?.id);

      if (index - 1 >= 0) {
        this.activeItem = this.filteredActions[index - 1];
      } else {
        this.activeItem = this.filteredActions.lastObject;
      }
    } else {
      this.activeItem = this.filteredActions.lastObject;
    }

    this.activeItem?.didBecomeActive();
  }

  @action setActiveItem(action: Action) {
    this.activeItem = action;
  }

  @action async executeSelectedAction(action?: Action) {
    if (action) {
      if (action instanceof NavigationAction) {
        this.intercomEventService.trackAnalyticsEvent({
          action: 'navigated',
          object: 'settings_search',
          search_term: this.searchTerm,
          selected_action: action.id,
          destination_route: action.route,
          selected_result: action.fullName,
        });
      }

      await action.onSelect();
      this.close();
    }
  }

  @action close(event?: KeyboardEvent) {
    // prevent the default cmd+k behavior. Firefox uses cmd+k to open the search bar
    event?.preventDefault();

    this.intercomEventService.trackAnalyticsEvent({
      action: 'closed',
      object: 'settings_search',
    });

    this.isVisible = false;
  }

  @action toggleVisibility(source = 'clicked', event?: KeyboardEvent) {
    if (this.appService.app.canUseFeature('team-product-guidance-global-search')) {
      this.searchStateService.toggleGlobalSearch();
    } else if (this.isVisible) {
      this.close(event);
    } else {
      this.open(source, event);
    }
  }

  private isAlreadyHandled(e?: KeyboardEvent): boolean {
    if (e?.defaultPrevented) {
      return true;
    }
    let active = document.activeElement as HTMLElement | null;
    return !!active?.closest('.ProseMirror');
  }

  @action open(source = 'clicked', e?: KeyboardEvent) {
    // prevent the default cmd+k behavior. Firefox uses cmd+k to open the search bar
    // don't allow cmd+K to open when a ProseMirror editor is focused (e.g. article editor uses Mod-k for hyperlinks)
    if (this.isAlreadyHandled(e) && source === 'cmd-k') {
      return;
    }
    e?.preventDefault();
    this.intercomEventService.trackAnalyticsEvent({
      action: 'opened',
      object: 'settings_search',
      opened_via: source,
    });

    this.searchTerm = '';
    this.flattenedActions.forEach((action) => {
      action.setQuery(this.searchTerm);
    });
    this.activeItem = undefined;
    this.isVisible = true;
  }

  @action updateSearch(term: string) {
    this.activeItem = undefined;

    this.searchTerm = term;

    this.flattenedActions.forEach((action) => {
      action.setQuery(this.searchTerm);
    });

    this.activeItem = this.filteredActions.firstObject;

    if (this.filteredActions.length === 1) {
      this.activeItem = this.filteredActions.firstObject;
    }

    taskFor(this.recordSearchEvent).perform();
  }

  @keepLatestTask *recordSearchEvent(): TaskGenerator<void> {
    yield timeout(ENV.APP._1S);

    this.intercomEventService.trackAnalyticsEvent({
      action: 'searched',
      object: 'settings_search',
      search_term: this.searchTerm,
      results: this.filteredActions.length,
    });
  }

  @action handleDocumentClick(event: MouseEvent) {
    if (
      !this.modal.contains(event.target as Node) &&
      !this.searchToggle.contains(event.target as Node)
    ) {
      this.close();
    }
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'global-search/settings/search-bar': typeof SettingsSearchBar;
    'GlobalSearch::Settings::SearchBar': typeof SettingsSearchBar;
  }
}
